import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { Map, Lock, Unlock, X, Route } from 'lucide-react';
import { Section, Card } from './primitives';

/*
 * Locations tab driven by data (see thiCvor.locations):
 *   maps[]        { id, title, caption, src }
 *   elevators{}   bank -> { label, color, x, y }   (x/y: percent position on the layout map)
 *   groups[]      { id, title, items: [{ name, floor, where?, elevator, floorMap?, code?, codeLabel?, codeNote?, note?, medmaps? }]  (floor stays short; room detail goes in `where`) }
 *   medmaps{}     { base, building, origins: { key: { id, label } } }; item.medmaps = { to, from? } (from defaults to 'cvor')
 * Tapping a row's elevator line opens the layout map with that bank circled.
 */

/** Full-screen map with pinch-zoom and an optional ring around one elevator bank */
const MapSheet = ({ map, marker, onClose }) =>
  ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 bg-black/90 flex flex-col animate-fadeIn" onClick={onClose}>
      <div className="flex items-center justify-between p-3 text-white/80">
        <div className="min-w-0">
          <div className="text-sm font-medium text-white truncate">{map.title}</div>
          {marker && <div className="text-xs truncate">Take the {marker.label} elevators</div>}
        </div>
        <button onClick={onClose} aria-label="Close" className="p-2 rounded-full hover:bg-white/10"><X size={22} /></button>
      </div>
      <div className="flex-1 overflow-auto flex items-center justify-center p-2" style={{ touchAction: 'pinch-zoom' }}>
        <div className="relative inline-block" onClick={(e) => e.stopPropagation()}>
          <img src={map.src} alt={map.title} className="max-w-full max-h-[82vh] object-contain block" />
          {marker && (
            <span
              aria-hidden="true"
              className="absolute w-9 h-9 -ml-[18px] -mt-[18px] rounded-full border-[3px] animate-pulse"
              style={{ left: `${marker.x}%`, top: `${marker.y}%`, borderColor: marker.color, boxShadow: `0 0 0 4px ${marker.color}55` }}
            />
          )}
        </div>
      </div>
    </div>,
    document.body
  );

const CodeBadge = ({ code, label, note }) => {
  const [shown, setShown] = useState(false);
  const title = label ? `${label} code` : 'Door code';
  return (
    <button
      onClick={() => { setShown((v) => !v); }}
      aria-label={shown ? `Hide ${title.toLowerCase()}` : `Show ${title.toLowerCase()}`}
      aria-pressed={shown}
      className={`inline-flex items-center gap-1.5 rounded-lg text-xs font-medium border transition-all duration-200 active:scale-95 ${
        shown
          ? 'px-2.5 py-1.5 bg-amber-500 border-amber-500 text-white shadow-md animate-pop'
          : 'px-2 py-1 bg-amber-50 border-amber-200 text-amber-900 dark:bg-amber-900/30 dark:border-amber-800 dark:text-amber-100'
      }`}
    >
      {shown ? <Unlock size={13} /> : <Lock size={12} />}
      {shown ? (
        <>
          <span className="font-mono text-[15px] font-bold tracking-wider">{code}</span>
          {note && <span className="text-white/80">· {note}</span>}
        </>
      ) : title}
    </button>
  );
};

/**
 * One card of location rows, with its own map sheet. Reused by the Locations tab
 * (one per group) and by other tools that list places, e.g. Airways & Codes.
 *   data: the hospital's `locations` block; items: rows to show; footer: optional node under the rows
 */
export const LocationRows = ({ data, items, footer }) => {
  const [sheet, setSheet] = useState(null); // { map, marker? }
  const mapById = Object.fromEntries((data.maps || []).map((m) => [m.id, m]));
  const layout = mapById.layout;

  const openElevatorMap = (bank) => {
    if (!layout) return;
    setSheet({ map: layout, marker: (data.elevators || {})[bank] });
  };
  const openMap = (map) => { setSheet({ map }); };

  const directions = (item) => {
    const cfg = data.medmaps;
    if (!cfg || !item.medmaps) return null;
    const origin = cfg.origins[item.medmaps.from || 'cvor'];
    if (!origin) return null;
    const slug = item.name.replace(/[^A-Za-z0-9]+/g, '_');
    return { label: `Directions from ${origin.label}`, href: `${cfg.base}?from=${origin.id}-${cfg.building}&to=${item.medmaps.to}#department_sms/${slug}` };
  };

  return (
    <>
      <Card>
      {items.map((item) => {
        const bank = (data.elevators || {})[item.elevator];
        return (
          <div key={item.name} className="px-4 py-3 flex items-center gap-3">
           <div className="flex-1 min-w-0">
            <div className="text-[15px] font-medium text-gray-900 dark:text-white leading-snug">{item.name}</div>
            {bank && layout ? (
              <button
                onClick={() => openElevatorMap(item.elevator)}
                className="mt-0.5 inline-flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300 text-left"
                aria-label={`Show the ${bank.label} elevators on the layout map`}
              >
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: bank.color }} aria-hidden="true" />
                <span>{item.floor} · {bank.label} elevators</span>
                <Map size={13} className="text-gray-400 flex-shrink-0" aria-hidden="true" />
              </button>
            ) : (
              <div className="mt-0.5 flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300">
                {bank && <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: bank.color }} aria-hidden="true" />}
                <span>{item.floor}{bank ? ` · ${bank.label} elevators` : ''}</span>
              </div>
            )}
            {[item.where, item.note].filter(Boolean).map((line) => (
              <div key={line} className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-snug">{line}</div>
            ))}
            {(item.code || (item.floorMap && mapById[item.floorMap])) && (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {item.code && <CodeBadge code={item.code} label={item.codeLabel} note={item.codeNote} />}
                {item.floorMap && mapById[item.floorMap] && (
                  <button onClick={() => openMap(mapById[item.floorMap])} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
                    <Map size={12} /> {mapById[item.floorMap].title} plan
                  </button>
                )}
              </div>
            )}
           </div>
           {directions(item) && (
             <a
               href={directions(item).href}
               target="_blank"
               rel="noopener noreferrer"
               onClick={() => {}}
               aria-label={directions(item).label}
               className="flex-shrink-0 flex flex-col items-center gap-1 text-gray-500 dark:text-gray-400 active:scale-95 transition-transform"
             >
               <span className="w-9 h-9 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center"><Route size={16} /></span>
               <span className="text-[10px] leading-none">Directions</span>
             </a>
           )}
          </div>
        );
      })}
      {footer}
      </Card>
      {sheet && <MapSheet map={sheet.map} marker={sheet.marker} onClose={() => setSheet(null)} />}
    </>
  );
};

const LocationList = ({ data }) => {
  const [sheet, setSheet] = useState(null);
  const openMap = (map) => { setSheet({ map }); };

  return (
    <div className="space-y-6">
      {/* Map strip */}
      {data.maps?.length > 0 && (
      <div className="flex gap-3 overflow-x-auto pb-1 -mx-6 px-6 snap-x snap-mandatory scroll-px-6 scrollbar-hide">
        {data.maps.map((m) => (
          <button key={m.id} onClick={() => openMap(m)} className="snap-start flex-shrink-0 w-44 text-left">
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-card overflow-hidden">
              <img src={m.src} alt={m.title} className="w-full h-24 object-cover object-center bg-gray-100" />
              <div className="px-3 py-2">
                <div className="text-sm font-medium text-gray-900 dark:text-white leading-tight">{m.title}</div>
                <div className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 truncate">{m.caption}</div>
              </div>
            </div>
          </button>
        ))}
      </div>
      )}

      {data.groups.map((group) => (
        <Section key={group.id} title={group.title}>
          <LocationRows data={data} items={group.items} />
        </Section>
      ))}

      {sheet && <MapSheet map={sheet.map} marker={sheet.marker} onClose={() => setSheet(null)} />}
    </div>
  );
};

export default LocationList;
